import { useEffect, useState } from "react";
import { t } from "@workspace/strings/web";
import { countdownConfig, countdownParts, pad2 } from "@/player/lib/widgets";

function Unit({ value, label }: { value: string; label: string }) {
  return (
    <div className="flex flex-col items-center gap-2">
      <div className="rounded-2xl bg-card border px-[2.5vw] py-[2vh] font-semibold tabular-nums text-foreground text-[9vw] leading-none">
        {value}
      </div>
      <div className="text-muted-foreground uppercase tracking-wide text-[1.6vw]">
        {label}
      </div>
    </div>
  );
}

/** Countdown to config.targetIso with an optional label (sale, opening, …). */
export function CountdownWidget({
  config,
}: {
  config: Record<string, unknown> | null;
}) {
  const cfg = countdownConfig(config);
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  if (!cfg) {
    return (
      <div
        className="flex h-full w-full items-center justify-center bg-background text-muted-foreground"
        data-testid="widget-countdown"
      >
        {t("player.widgets.countdown.needsTarget")}
      </div>
    );
  }

  const parts = countdownParts(cfg.targetIso, now);
  return (
    <div
      className="flex h-full w-full flex-col items-center justify-center gap-[4vh] bg-background"
      data-testid="widget-countdown"
    >
      {cfg.label && (
        <div className="text-foreground text-[4vw] font-medium">{cfg.label}</div>
      )}
      {parts.done ? (
        <div className="text-primary text-[9vw] font-semibold">
          {cfg.doneText}
        </div>
      ) : (
        <div className="flex items-start gap-[2vw]">
          {parts.days > 0 && (
            <Unit value={String(parts.days)} label={t("player.widgets.countdown.unit.days")} />
          )}
          <Unit value={pad2(parts.hours)} label={t("player.widgets.countdown.unit.hours")} />
          <Unit value={pad2(parts.minutes)} label={t("player.widgets.countdown.unit.minutes")} />
          <Unit value={pad2(parts.seconds)} label={t("player.widgets.countdown.unit.seconds")} />
        </div>
      )}
    </div>
  );
}
